import { useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, More } from '@peacecover/icon';
import { Virtuoso } from 'react-virtuoso';
import { useGetAdaptedFictionEditionReplies } from '../../domains/scene/ui/hooks/useGetAdaptedFictionEditionReplies';
import { useDeleteAdaptedFictionEditionComment } from '../../domains/scene/ui/hooks/useDeleteAdaptedFictionEditionComment';
import { useHandleHistoryBack } from '../../utils/useHandleHistoryBack';
import { CommentDetailParentCommentItem } from './components/CommentDetailParentCommentItem';
import { CommentDetailReplyItem } from './components/CommentDetailReplyItem';
import { CommentDetailReplyInputField } from './components/CommentDetailReplyInputField';
import { DeleteMenuDropdown } from './components/DeleteMenuDropdown';
import type { components } from '../../types/schema';

const SceneCommentsDetailPage = () => {
  const { adaptedFictionEditionId, commentId } = useParams();
  const handleHistoryBack = useHandleHistoryBack();
  const [searchParams] = useSearchParams();
  const adaptedFictionPreviewId = searchParams.get('adaptedFictionPreviewId') ?? undefined;
  const sceneId = searchParams.get('sceneId') ?? undefined;

  const safeAdaptedFictionEditionId = adaptedFictionEditionId ?? '';
  const safeCommentId = commentId ?? '';

  const {
    data: repliesPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useGetAdaptedFictionEditionReplies({
    adaptedFictionEditionId: safeAdaptedFictionEditionId,
    commentId: safeCommentId,
    params: {
      per_page: 20
    }
  });

  const replies = useMemo(() => {
    const flattened = repliesPages?.pages.flatMap((page) => page.comments ?? []) ?? [];
    return flattened;
  }, [repliesPages]);

  const { mutateAsync: deleteAdaptedFictionEditionComment } = useDeleteAdaptedFictionEditionComment(
    {
      adaptedFictionEditionId: safeAdaptedFictionEditionId,
      commentId: safeCommentId
    }
  );

  const handleDeleteAdaptedFictionEditionComment = async () => {
    await deleteAdaptedFictionEditionComment();
    handleHistoryBack();
  };

  return (
    <div className="min-h-screen pt-[env(safe-area-inset-top)] pb-[calc(env(safe-area-inset-bottom)+100px)]">
      <header className="px-[16px] py-[15px] flex items-center justify-between text-text-main-lv1 relative">
        <button type="button" onClick={handleHistoryBack} aria-label="뒤로가기">
          <ArrowLeft />
        </button>
        <DeleteMenuDropdown
          trigger={<More />}
          dropdownClassName="absolute bottom-[-39px] right-[27px]"
          onDelete={handleDeleteAdaptedFictionEditionComment}
          stopPropagationOnMouseDown
        />
      </header>
      <CommentDetailParentCommentItem
        adaptedFictionEditionId={safeAdaptedFictionEditionId}
        commentId={safeCommentId}
      />
      <div className="flex flex-col">
        <Virtuoso
          useWindowScroll
          overscan={480}
          defaultItemHeight={120}
          style={{ minHeight: 100 }}
          data={replies}
          endReached={() => {
            if (!hasNextPage || isFetchingNextPage) return;
            fetchNextPage();
          }}
          itemContent={(
            _index,
            reply: components['schemas']['ApiV1AdaptedFictionEditionComment']
          ) => (
            <CommentDetailReplyItem
              reply={reply}
              adaptedFictionEditionId={safeAdaptedFictionEditionId}
              threadCommentId={safeCommentId}
              adaptedFictionPreviewId={adaptedFictionPreviewId}
              sceneId={sceneId}
            />
          )}
        />
      </div>
      <CommentDetailReplyInputField
        adaptedFictionEditionId={safeAdaptedFictionEditionId}
        threadCommentId={safeCommentId}
        adaptedFictionPreviewId={adaptedFictionPreviewId}
        sceneId={sceneId}
      />
    </div>
  );
};

export default SceneCommentsDetailPage;
